<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import LeadAlertsAPI from 'dashboard/api/leadAlerts';
import PropertyDemandsAPI from 'dashboard/api/propertyDemands';
import PropertyMatchesAPI from 'dashboard/api/propertyMatches';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LeadsListState from './LeadsListState.vue';
import PropertyMatchBreakdown from './PropertyMatchBreakdown.vue';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  contactId: { type: [String, Number], required: true },
  notificationGuard: { type: Object, default: () => ({ ready: false }) },
});

const emit = defineEmits(['changed']);

const IMPORTANT_FIELDS = [
  'title',
  'modalidade',
  'objective',
  'budget_min',
  'budget_max',
  'regions',
  'property_types',
  'bedrooms',
  'payment_kind',
  'area_min',
  'area_max',
  'status',
];

const FIELD_LABELS = {
  title: 'Título',
  modalidade: 'Operação',
  objective: 'Objetivo',
  budget_min: 'Orçamento mínimo',
  budget_max: 'Orçamento máximo',
  regions: 'Regiões',
  property_types: 'Tipos',
  bedrooms: 'Quartos',
  payment_kind: 'Pagamento',
  area_min: 'Área mínima',
  area_max: 'Área máxima',
  status: 'Status',
};

const MODALIDADE_LABELS = {
  venda: 'Compra',
  aluguel: 'Locação',
  temporada: 'Temporada',
};

const OBJECTIVE_LABELS = {
  home: 'Moradia',
  investment: 'Investimento',
  family: 'Família',
  relocation: 'Mudança',
  temporary: 'Temporário',
  other: 'Outro',
};

const STATUS_LABELS = {
  active: 'Ativa',
  paused: 'Pausada',
  satisfied: 'Satisfeita',
  expired: 'Expirada',
  discarded: 'Descartada',
};

const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { id: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { id: 'sms', label: 'SMS', icon: 'i-lucide-smartphone' },
];

const FREQUENCY_LABELS = {
  always: 'Sempre',
  daily: 'Diário',
  weekly: 'Semanal',
};

const defaultDemandForm = () => ({
  title: '',
  modalidade: 'venda',
  objective: 'home',
  budget_min: '',
  budget_max: '',
  regions: '',
  property_types: '',
  bedrooms: '',
  reason: '',
});

const defaultAlertForm = () => ({
  title: '',
  delivery_channels: ['whatsapp'],
  delivery_frequency: 'daily',
  delivery_note: '',
});

const demands = ref([]);
const demandDetail = ref(null);
const matches = ref([]);
const alerts = ref([]);
const selectedDemandId = ref(null);
const demandForm = ref(defaultDemandForm());
const alertForm = ref(defaultAlertForm());
const demandLoadState = ref('loading');
const demandDetailState = ref('idle');
const matchesLoadState = ref('idle');
const alertsLoadState = ref('loading');
const isSavingDemand = ref(false);
const isCreatingAlert = ref(false);

const collectionFromResponse = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.payload)) return data.payload;
  return [];
};

const stateFromError = error =>
  Number(error?.response?.status || error?.status) === 403
    ? 'forbidden'
    : 'error';

const normalizeModalidade = value => {
  const raw = String(value || '').toLowerCase();
  if (['aluguel', 'locacao', 'locação', 'rent', 'rental'].includes(raw)) {
    return 'aluguel';
  }
  if (raw === 'temporada') return 'temporada';
  return 'venda';
};

const selectedDemand = computed(() => {
  if (demandDetail.value?.id === selectedDemandId.value) {
    return demandDetail.value;
  }
  return demands.value.find(demand => demand.id === selectedDemandId.value);
});

const selectedDemandVersions = computed(
  () => demandDetail.value?.versions || []
);
const latestVersion = computed(
  () => selectedDemandVersions.value.at(-1) || null
);
const previousVersion = computed(() =>
  selectedDemandVersions.value.length > 1
    ? selectedDemandVersions.value.at(-2)
    : null
);
const editingExistingDemand = computed(() => Boolean(selectedDemand.value?.id));
const hasDemandError = computed(() =>
  ['error', 'forbidden'].includes(demandLoadState.value)
);
const hasAlertsError = computed(() =>
  ['error', 'forbidden'].includes(alertsLoadState.value)
);
const matchesError = computed(() => matchesLoadState.value === 'error');
const matchesPermissionError = computed(
  () => matchesLoadState.value === 'forbidden'
);
const canMutateDemand = computed(() => demandLoadState.value !== 'forbidden');
const alertGuardReady = computed(
  () =>
    props.notificationGuard?.ready &&
    props.notificationGuard?.purpose === 'NOTIMANAGER'
);

const valueList = value => {
  if (Array.isArray(value)) return value;
  return String(value || '')
    .split(/[,;\n]/)
    .map(item => item.trim())
    .filter(Boolean);
};

const bedroomsList = value =>
  valueList(value)
    .map(item => Number.parseInt(item, 10))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(number) ? number : null;
};

const moneyLabel = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Não informado';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
};

const valueLabel = (field, value) => {
  if (value === null || value === undefined || value === '') {
    return 'Não informado';
  }
  if (['budget_min', 'budget_max'].includes(field)) return moneyLabel(value);
  if (field === 'modalidade') return MODALIDADE_LABELS[value] || value;
  if (field === 'objective') return OBJECTIVE_LABELS[value] || value;
  if (field === 'status') return STATUS_LABELS[value] || value;
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Nenhum';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const dateTime = value => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString('pt-BR');
};

const demandTitle = demand =>
  demand?.title || demand?.demand_key || `Demanda #${demand?.id}`;

const applyDemandToForm = demand => {
  const attrs = props.contact?.additional_attributes || {};
  demandForm.value = {
    title: demand?.title || '',
    modalidade: normalizeModalidade(demand?.modalidade || attrs.operacao),
    objective: demand?.objective || 'home',
    budget_min: demand?.budget_min || '',
    budget_max: demand?.budget_max || '',
    regions: (demand?.regions || []).join(', '),
    property_types: (demand?.property_types || []).join(', '),
    bedrooms: (demand?.bedrooms || []).join(', '),
    reason: '',
  };
};

const mergeDemand = demand => {
  demands.value = [
    demand,
    ...demands.value.filter(item => item.id !== demand.id),
  ].sort((a, b) =>
    String(b.updated_at || '').localeCompare(a.updated_at || '')
  );
};

const demandPayload = () => ({
  title: demandForm.value.title.trim() || null,
  modalidade: demandForm.value.modalidade,
  objective: demandForm.value.objective,
  budget_min: numberOrNull(demandForm.value.budget_min),
  budget_max: numberOrNull(demandForm.value.budget_max),
  regions: valueList(demandForm.value.regions),
  property_types: valueList(demandForm.value.property_types),
  bedrooms: bedroomsList(demandForm.value.bedrooms),
  source: selectedDemand.value?.source || 'manual',
  reason: demandForm.value.reason.trim(),
});

const versionDiffRows = computed(() => {
  if (!latestVersion.value) return [];

  const fields = latestVersion.value.changed_fields?.length
    ? latestVersion.value.changed_fields
    : IMPORTANT_FIELDS;
  const previous = previousVersion.value?.snapshot || {};
  const current = latestVersion.value.snapshot || selectedDemand.value || {};

  return fields
    .filter(field => IMPORTANT_FIELDS.includes(field))
    .map(field => ({
      field,
      label: FIELD_LABELS[field] || field,
      previous: valueLabel(field, previous[field]),
      current: valueLabel(field, current[field]),
    }));
});

const loadMatches = async demandId => {
  if (!demandId) {
    matches.value = [];
    matchesLoadState.value = 'empty';
    return;
  }

  matchesLoadState.value = 'loading';
  try {
    const { data } = await PropertyMatchesAPI.list({
      contact_id: props.contactId,
      property_demand_id: demandId,
      page: 1,
    });
    matches.value = collectionFromResponse(data);
    matchesLoadState.value = matches.value.length ? 'ready' : 'empty';
  } catch (error) {
    matches.value = [];
    matchesLoadState.value = stateFromError(error);
  }
};

const loadDemandDetail = async demandId => {
  if (!demandId) return;

  demandDetailState.value = 'loading';
  try {
    const { data } = await PropertyDemandsAPI.show(demandId);
    demandDetail.value = data || null;
    if (data?.id) {
      selectedDemandId.value = data.id;
      mergeDemand(data);
      applyDemandToForm(data);
    }
    demandDetailState.value = 'ready';
    await loadMatches(demandId);
  } catch (error) {
    demandDetail.value = null;
    demandDetailState.value = stateFromError(error);
    await loadMatches(null);
  }
};

const loadDemands = async () => {
  demandLoadState.value = 'loading';
  try {
    const { data } = await PropertyDemandsAPI.listForContact(props.contactId, {
      page: 1,
    });
    demands.value = collectionFromResponse(data);
    demandLoadState.value = demands.value.length ? 'ready' : 'empty';
    const nextDemand =
      demands.value.find(demand => demand.id === selectedDemandId.value) ||
      demands.value[0];
    selectedDemandId.value = nextDemand?.id || null;
    if (nextDemand) await loadDemandDetail(nextDemand.id);
    else {
      demandDetail.value = null;
      applyDemandToForm(null);
      await loadMatches(null);
    }
  } catch (error) {
    demands.value = [];
    demandDetail.value = null;
    demandLoadState.value = stateFromError(error);
    await loadMatches(null);
  }
};

const loadAlerts = async () => {
  alertsLoadState.value = 'loading';
  try {
    const { data } = await LeadAlertsAPI.list({ contactId: props.contactId });
    alerts.value = collectionFromResponse(data);
    alertsLoadState.value = alerts.value.length ? 'ready' : 'empty';
  } catch (error) {
    alerts.value = [];
    alertsLoadState.value = stateFromError(error);
  }
};

const loadContext = async () => {
  await Promise.all([loadDemands(), loadAlerts()]);
};

const selectDemand = async demand => {
  selectedDemandId.value = demand.id;
  await loadDemandDetail(demand.id);
};

const startNewDemand = () => {
  selectedDemandId.value = null;
  demandDetail.value = null;
  matches.value = [];
  matchesLoadState.value = 'empty';
  applyDemandToForm(null);
};

const saveDemand = async () => {
  if (!canMutateDemand.value) {
    useAlert('Sem permissão para alterar demandas deste cliente.');
    return;
  }
  if (!demandForm.value.reason.trim()) {
    useAlert('Informe o motivo da alteração da demanda.');
    return;
  }

  isSavingDemand.value = true;
  try {
    const payload = demandPayload();
    const request = editingExistingDemand.value
      ? PropertyDemandsAPI.update(selectedDemand.value.id, payload)
      : PropertyDemandsAPI.createForContact(props.contactId, payload);
    const { data } = await request;
    demandDetail.value = data || null;
    if (data?.id) {
      selectedDemandId.value = data.id;
      mergeDemand(data);
      applyDemandToForm(data);
      await loadMatches(data.id);
    }
    useAlert('Demanda versionada salva.');
    emit('changed');
  } catch (error) {
    if (Number(error?.response?.status || error?.status) === 409) {
      useAlert(
        'Conflito de versão detectado. Recarreguei a demanda antes de salvar novamente.'
      );
      if (selectedDemand.value?.id)
        await loadDemandDetail(selectedDemand.value.id);
      return;
    }
    useAlert(
      error.response?.data?.errors?.[0] ||
        error.response?.data?.error ||
        'Não foi possível salvar a demanda.'
    );
  } finally {
    isSavingDemand.value = false;
  }
};

const alertChannelGuard = channel => {
  if (channel !== 'whatsapp') return { allowed: true };
  if (!alertGuardReady.value) {
    return {
      allowed: false,
      message:
        'Aguardando guard NOTIMANAGER antes de ativar alerta por WhatsApp.',
    };
  }
  return (
    props.notificationGuard.checks?.find(check => check.id === 'whatsapp') || {
      allowed: false,
      message: 'Guard de WhatsApp indisponível para alertas de imóveis.',
    }
  );
};

const blockedAlertChannels = computed(() =>
  alertForm.value.delivery_channels
    .map(channel => ({ channel, guard: alertChannelGuard(channel) }))
    .filter(item => !item.guard.allowed)
);

const toggleAlertChannel = channel => {
  const next = new Set(alertForm.value.delivery_channels);
  if (next.has(channel)) next.delete(channel);
  else next.add(channel);
  alertForm.value.delivery_channels = Array.from(next);
};

const alertPayload = () => {
  const demand = selectedDemand.value || {};
  const regions = demand.regions || valueList(demandForm.value.regions);
  const propertyTypes =
    demand.property_types || valueList(demandForm.value.property_types);
  const bedrooms = demand.bedrooms || bedroomsList(demandForm.value.bedrooms);

  return {
    contact_id: props.contactId,
    property_demand_id: demand.id,
    title:
      alertForm.value.title.trim() ||
      `Busca salva - ${demandTitle(demand || props.contact)}`,
    finalidade: demand.modalidade || demandForm.value.modalidade,
    cidade: regions[0] || null,
    bairro: regions[0] || null,
    tipo: propertyTypes[0] || null,
    quartos_min: bedrooms[0] || null,
    preco_min: demand.budget_min || numberOrNull(demandForm.value.budget_min),
    preco_max: demand.budget_max || numberOrNull(demandForm.value.budget_max),
    status: 'active',
    filters: {
      delivery_channels: alertForm.value.delivery_channels,
      delivery_frequency: alertForm.value.delivery_frequency,
      delivery_note: alertForm.value.delivery_note.trim(),
      source: 'lead_demand_context',
      consent_purpose: 'NOTIMANAGER',
    },
  };
};

const createAlert = async () => {
  if (!selectedDemand.value?.id) {
    useAlert('Salve uma demanda antes de criar uma busca salva.');
    return;
  }
  if (!alertForm.value.delivery_channels.length) {
    useAlert('Selecione ao menos um canal para o alerta.');
    return;
  }
  if (blockedAlertChannels.value.length) {
    useAlert(blockedAlertChannels.value[0].guard.message);
    return;
  }

  isCreatingAlert.value = true;
  try {
    const { data } = await LeadAlertsAPI.create(alertPayload());
    alerts.value = [
      data,
      ...alerts.value.filter(alert => alert.id !== data.id),
    ];
    alertsLoadState.value = 'ready';
    alertForm.value = defaultAlertForm();
    useAlert('Busca salva criada para este cliente.');
    emit('changed');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        error.response?.data?.error ||
        'Não foi possível criar a busca salva.'
    );
  } finally {
    isCreatingAlert.value = false;
  }
};

watch(
  () => props.contactId,
  () => {
    selectedDemandId.value = null;
    loadContext();
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="grid gap-4" data-testid="lead-demand-context-panel">
    <div
      class="flex flex-col gap-3 rounded-lais-sm border border-[#eee5d6] bg-white p-4"
    >
      <div
        class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
      >
        <div>
          <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
            Demanda versionada
          </p>
          <h2 class="mt-1 text-[16px] font-semibold text-lais-text">
            Preferências, matching e alertas do lead
          </h2>
          <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
            Edite a demanda com motivo, compare versões e crie buscas salvas sem
            sair do cliente.
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            icon="i-lucide-refresh-cw"
            size="sm"
            variant="ghost"
            :is-loading="demandLoadState === 'loading'"
            @click="loadContext"
          >
            Atualizar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-plus"
            size="sm"
            variant="ghost"
            @click="startNewDemand"
          >
            Nova demanda
          </LaisPrimaryButton>
        </div>
      </div>

      <div
        v-if="demandLoadState === 'loading'"
        class="rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4 text-[13px] font-semibold text-lais-text-muted"
      >
        Carregando demanda do cliente…
      </div>
      <LeadsListState
        v-else-if="demandLoadState === 'empty'"
        title="Nenhuma demanda versionada"
        description="Crie a primeira demanda real deste cliente para alimentar matching e alertas."
        icon="i-lucide-list-plus"
      />
      <LeadsListState
        v-else-if="hasDemandError"
        :title="
          demandLoadState === 'forbidden'
            ? 'Sem permissão para gerenciar demanda'
            : 'Demanda indisponível'
        "
        :description="
          demandLoadState === 'forbidden'
            ? 'Seu perfil não possui permissão para ler ou editar demandas deste cliente.'
            : 'Não foi possível carregar as demandas versionadas do cliente.'
        "
        icon="i-lucide-shield-alert"
        :critical="demandLoadState === 'forbidden'"
      />

      <div v-if="demands.length" class="flex flex-wrap gap-2">
        <button
          v-for="demand in demands"
          :key="demand.id"
          type="button"
          class="inline-flex min-h-9 max-w-full items-center gap-2 rounded-lais border px-3 text-[12px] font-semibold transition-colors"
          :class="
            selectedDemandId === demand.id
              ? 'border-lais-primary bg-[#f8f4fc] text-lais-primary'
              : 'border-[#e5e7eb] bg-white text-lais-text hover:border-lais-primary'
          "
          @click="selectDemand(demand)"
        >
          <span class="i-lucide-list-checks size-4" aria-hidden="true" />
          <span class="truncate">{{ demandTitle(demand) }}</span>
          <span class="shrink-0 text-[11px] opacity-75">
            v{{ demand.latest_version_number || 1 }}
          </span>
        </button>
      </div>

      <form class="grid gap-3" @submit.prevent="saveDemand">
        <div class="grid gap-3 md:grid-cols-2">
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Título
            <input
              v-model="demandForm.title"
              type="text"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Compra para morar perto do trabalho"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Motivo da versão
            <input
              v-model="demandForm.reason"
              data-testid="demand-reason-input"
              type="text"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Cliente mudou orçamento, bairro ou tipo"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Operação
            <select
              v-model="demandForm.modalidade"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="venda">Compra</option>
              <option value="aluguel">Locação</option>
              <option value="temporada">Temporada</option>
            </select>
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Objetivo
            <select
              v-model="demandForm.objective"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="home">Moradia</option>
              <option value="investment">Investimento</option>
              <option value="family">Família</option>
              <option value="relocation">Mudança</option>
              <option value="temporary">Temporário</option>
              <option value="other">Outro</option>
            </select>
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Orçamento mínimo
            <input
              v-model="demandForm.budget_min"
              type="number"
              min="0"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="500000"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Orçamento máximo
            <input
              v-model="demandForm.budget_max"
              data-testid="demand-budget-max-input"
              type="number"
              min="0"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="900000"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Regiões
            <input
              v-model="demandForm.regions"
              type="text"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Botafogo, Flamengo"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Tipos e quartos
            <div class="grid gap-2 sm:grid-cols-[1fr_120px]">
              <input
                v-model="demandForm.property_types"
                type="text"
                class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                placeholder="apartamento, cobertura"
              />
              <input
                v-model="demandForm.bedrooms"
                type="text"
                class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                placeholder="2, 3"
              />
            </div>
          </label>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-[12px] text-lais-text-muted">
            {{
              editingExistingDemand
                ? 'Salvar cria uma nova versão auditável da demanda selecionada.'
                : 'Criar demanda grava o primeiro snapshot versionado do cliente.'
            }}
          </p>
          <LaisPrimaryButton
            data-testid="save-demand-button"
            icon="i-lucide-save"
            size="sm"
            :is-loading="isSavingDemand"
            :disabled="!canMutateDemand || isSavingDemand"
            @click="saveDemand"
          >
            Salvar demanda versionada
          </LaisPrimaryButton>
        </div>
      </form>

      <section
        v-if="latestVersion"
        class="rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Comparação de versões
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              v{{ previousVersion?.version_number || 'inicial' }} → v{{
                latestVersion.version_number
              }}
            </p>
          </div>
          <p class="text-[12px] text-lais-text-muted">
            {{ dateTime(latestVersion.occurred_at) }}
          </p>
        </div>
        <p
          v-if="latestVersion.reason"
          class="mt-2 text-[13px] font-semibold text-lais-primary"
        >
          Motivo: {{ latestVersion.reason }}
        </p>
        <dl v-if="versionDiffRows.length" class="mt-3 grid gap-2">
          <div
            v-for="row in versionDiffRows"
            :key="row.field"
            class="grid gap-2 rounded-lais-sm border border-[#e5e7eb] bg-white p-3 text-[12px] sm:grid-cols-[140px_1fr_1fr]"
          >
            <dt class="font-semibold text-lais-text">{{ row.label }}</dt>
            <dd class="text-lais-text-muted">Antes: {{ row.previous }}</dd>
            <dd class="font-semibold text-lais-text">
              Agora: {{ row.current }}
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <section
        class="rounded-lais-sm border border-[#eee5d6] bg-white p-4"
        data-testid="lead-demand-matches"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Matching explicável
            </p>
            <h3 class="mt-1 text-[15px] font-semibold text-lais-text">
              Imóveis sugeridos pela demanda
            </h3>
          </div>
          <span
            v-if="selectedDemand"
            class="rounded-full bg-[#f8f4fc] px-2 py-1 text-[11px] font-semibold text-lais-primary"
          >
            {{
              MODALIDADE_LABELS[selectedDemand.modalidade] ||
              selectedDemand.modalidade
            }}
          </span>
        </div>

        <div
          v-if="matchesLoadState === 'loading'"
          class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4 text-[13px] font-semibold text-lais-text-muted"
        >
          Carregando matching…
        </div>
        <LeadsListState
          v-else-if="matchesLoadState === 'empty'"
          title="Sem imóveis compatíveis persistidos"
          description="Nenhum PropertyMatch foi encontrado para a demanda selecionada."
          icon="i-lucide-home"
        />
        <LeadsListState
          v-else-if="matchesError || matchesPermissionError"
          :title="
            matchesPermissionError
              ? 'Sem permissão para ver matching'
              : 'Matching indisponível'
          "
          :description="
            matchesPermissionError
              ? 'Seu perfil não possui permissão para consultar matches deste lead.'
              : 'Não foi possível carregar os imóveis sugeridos.'
          "
          icon="i-lucide-shield-alert"
          :critical="matchesPermissionError"
        />
        <ul v-else class="mt-4 grid gap-3">
          <li
            v-for="match in matches"
            :key="match.id"
            class="rounded-lais-sm border border-[#eee5d6] p-4"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <h4 class="truncate text-[14px] font-semibold text-lais-text">
                  {{ match.imovel?.titulo || `Imóvel #${match.imovel_id}` }}
                </h4>
                <p class="mt-1 text-[12px] text-lais-text-body">
                  {{
                    [match.imovel?.bairro, match.imovel?.cidade]
                      .filter(Boolean)
                      .join(' · ') || 'Localização não informada'
                  }}
                </p>
              </div>
              <span
                class="rounded-full bg-[#EAF6F1] px-2 py-1 text-[11px] font-semibold text-[#176B55]"
              >
                Score {{ Math.round(Number(match.score || 0)) }}/100
              </span>
            </div>
            <PropertyMatchBreakdown :match="match" compact class="mt-3" />
          </li>
        </ul>
      </section>

      <section
        class="rounded-lais-sm border border-[#eee5d6] bg-white p-4"
        data-testid="lead-demand-alerts"
      >
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Buscas salvas e alertas
        </p>
        <h3 class="mt-1 text-[15px] font-semibold text-lais-text">
          Alertas no contexto do lead
        </h3>

        <div
          v-if="blockedAlertChannels.length"
          class="mt-3 rounded-lais-sm border border-[#f7c5c5] bg-[#fff5f5] p-3 text-[12px] font-semibold text-[#9f1239]"
        >
          {{ blockedAlertChannels[0].guard.message }}
        </div>

        <form class="mt-4 grid gap-3" @submit.prevent="createAlert">
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Nome da busca
            <input
              v-model="alertForm.title"
              type="text"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Novos imóveis compatíveis"
            />
          </label>
          <div class="grid gap-2">
            <p class="text-[12px] font-semibold text-lais-text">Canais</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="channel in CHANNELS"
                :key="channel.id"
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-lais border px-3 text-[12px] font-semibold transition-colors"
                :class="
                  alertForm.delivery_channels.includes(channel.id)
                    ? 'border-lais-primary bg-[#f8f4fc] text-lais-primary'
                    : 'border-[#e5e7eb] text-lais-text-muted hover:border-lais-primary'
                "
                @click="toggleAlertChannel(channel.id)"
              >
                <span class="size-4" :class="channel.icon" aria-hidden="true" />
                {{ channel.label }}
              </button>
            </div>
          </div>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Frequência
            <select
              v-model="alertForm.delivery_frequency"
              class="min-h-10 rounded-lais-sm border border-[#e5e7eb] px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="always">Sempre</option>
              <option value="daily">Diário</option>
              <option value="weekly">Semanal</option>
            </select>
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Observação
            <textarea
              v-model="alertForm.delivery_note"
              rows="3"
              class="resize-none rounded-lais-sm border border-[#e5e7eb] px-3 py-2 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Contexto para o corretor"
            />
          </label>
          <LaisPrimaryButton
            data-testid="create-alert-button"
            icon="i-lucide-bell-plus"
            size="sm"
            :is-loading="isCreatingAlert"
            :disabled="isCreatingAlert"
            @click="createAlert"
          >
            Criar busca salva
          </LaisPrimaryButton>
        </form>

        <div
          v-if="alertsLoadState === 'loading'"
          class="mt-4 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4 text-[13px] font-semibold text-lais-text-muted"
        >
          Carregando alertas…
        </div>
        <LeadsListState
          v-else-if="alertsLoadState === 'empty'"
          title="Nenhum alerta salvo"
          description="Crie uma busca salva ligada à demanda para acompanhar novos imóveis."
          icon="i-lucide-bell"
        />
        <LeadsListState
          v-else-if="hasAlertsError"
          :title="
            alertsLoadState === 'forbidden'
              ? 'Sem permissão para ver alertas'
              : 'Alertas indisponíveis'
          "
          :description="
            alertsLoadState === 'forbidden'
              ? 'Seu perfil não possui permissão para consultar alertas deste cliente.'
              : 'Não foi possível carregar as buscas salvas.'
          "
          icon="i-lucide-shield-alert"
          :critical="alertsLoadState === 'forbidden'"
        />
        <ul v-else class="mt-4 grid gap-2">
          <li
            v-for="alert in alerts"
            :key="alert.id"
            class="rounded-lais-sm border border-[#eee5d6] p-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="truncate text-[13px] font-semibold text-lais-text">
                  {{ alert.title || `Alerta #${alert.id}` }}
                </p>
                <p class="mt-1 text-[11px] text-lais-text-muted">
                  v{{ alert.alert_version }} ·
                  {{
                    (alert.delivery_channels || [])
                      .map(
                        channel =>
                          CHANNELS.find(item => item.id === channel)?.label ||
                          channel
                      )
                      .join(', ')
                  }}
                </p>
              </div>
              <span
                class="rounded-full bg-[#f5f5f5] px-2 py-1 text-[11px] font-semibold text-lais-text-body"
              >
                {{ STATUS_LABELS[alert.status] || alert.status }}
              </span>
            </div>
            <p class="mt-2 text-[12px] text-lais-text-body">
              Frequência:
              {{
                FREQUENCY_LABELS[alert.delivery_frequency] ||
                alert.delivery_frequency
              }}
            </p>
          </li>
        </ul>
      </section>
    </div>
  </section>
</template>
